import { useTranslation } from 'react-i18next';
import { CheckBoxSelect, Option, Pagination } from '@/components';
import { Text, Input } from '@/ui';
import { useGetResidualsReportByQueryQuery } from '@/store/slices/pricingSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { debounce } from '@/utils/helpers';
import { useGetUser } from '@/hooks';
import styles from './styles.module.css';
import { ResidualStatus } from '../pricing-forms/pricingHelpers';
import { EmptyResiduals } from './components/EmptyResiduals';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import FinalUserResidualsTable from './components/FinalUserResidualsTable';

type FinalUserFilter = {
  limit: number;
  offset: number;
};

function FinalUserResidualsList() {
  const { t } = useTranslation();
  const { company } = useGetUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<Option[]>([]);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

  const { filter, page, setPage, perPage, setPerPage } = usePaginationFilter<FinalUserFilter>({
    limit: 10,
    offset: 0,
  });

  const statusOptions: Option[] = [
    {
      id: ResidualStatus.DUE,
      label: t('Residuals.status_due'),
      value: ResidualStatus.DUE,
    },
    {
      id: ResidualStatus.ACCRUING,
      label: t('Residuals.status_accruing'),
      value: ResidualStatus.ACCRUING,
    },
    {
      id: ResidualStatus.PAID,
      label: t('Residuals.status_paid'),
      value: ResidualStatus.PAID,
    },
  ];

  const { data, isLoading, isError } = useGetResidualsReportByQueryQuery(
    company?._id
      ? {
          companyId: company._id,
          query: {
            searchValue: searchQuery || undefined,
            status:
              selectedStatusFilters.length === 0 ||
              selectedStatusFilters.length === statusOptions.length
                ? [ResidualStatus.DUE, ResidualStatus.ACCRUING, ResidualStatus.PAID]
                : selectedStatusFilters.map((opt) => opt.id),
            limit: filter.limit,
            offset: filter.offset,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
          },
        }
      : skipToken,
  );

  if (!company?._id) {
    return null;
  }

  if (isError) {
    return (
      <div>
        <Text size='s' weight='light-medium'>
          {t('Residuals.error_loading') || 'Error loading residuals'}
        </Text>
      </div>
    );
  }

  const handleSearch = debounce((value: string) => {
    setSearchQuery(value.toLowerCase());
    setPage(1);
  }, 300);

  const handleStatusChange = (newSelected: Option[]) => {
    setSelectedStatusFilters(newSelected);
    setPage(1);
  };

  const handleSetPerPage = (value: number | string) => {
    setPerPage(Number(value));
    setPage(1);
  };

  const handleSort = (newSortBy: string | null, newSortOrder: 'asc' | 'desc' | null) => {
    setPage(1);
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const residualsData = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;

  return (
    <>
      <div className={styles.filtersContainer}>
        <div className={styles.searchInput}>
          <Input
            size='small'
            variant='search'
            placeholder={t('Residuals.search_placeholder') || 'Search...'}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <div className={styles.selectContainer}>
          <CheckBoxSelect
            text={t('Residuals.select_all')}
            emptyText={t('Shared.select_status')}
            options={statusOptions}
            selected={selectedStatusFilters}
            onChange={handleStatusChange}
            withIcon={false}
            withArrow={true}
          />
        </div>
      </div>

      {isLoading ? (
        <EmptyResiduals />
      ) : residualsData.length === 0 ? (
        <EmptyResiduals />
      ) : (
        <FinalUserResidualsTable
          data={residualsData}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      )}
      {totalCount >= 10 && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalCount / Number(perPage))}
          setPage={setPage}
          perPage={String(perPage)}
          setPerPage={handleSetPerPage}
          maxResults={totalCount}
        />
      )}
    </>
  );
}

export default FinalUserResidualsList;
